// ------------------ IMPORTS -------------------

// Utils / Config / Services
import { DateTime, Info } from "luxon";

// Components
import { Tooltip } from "@lockerpm/design";

// Others
import { useTranslation } from "react-i18next";
import { IPassport } from "#src/services/endpoint/passports/types";
import { PassportPeriodType } from "#src/services/endpoint/passports/enums";

// ------------------ TYPES -------------------

interface TimeFrameRenderResult {
  content: JSX.Element;
}

// ------------------ UTILITY -------------------

export const useTimeFrameRenderer = () => {
  const { t, i18n } = useTranslation("endpoint", { keyPrefix: "passports" });

  const weekDayOptionsFull = Info.weekdays("long", {
    locale: i18n.language,
  }).map((day, index) => ({
    value: index,
    label: day,
  }));

  const renderTimeFrame = (item: IPassport): TimeFrameRenderResult => {
    if (!item.limit_time) {
      return {
        content: <span>N/A</span>,
      };
    }

    let verdictText = "";
    let tooltipText = "";

    // Daily
    if (item.duration_type === PassportPeriodType.DAILY) {
      verdictText = t("modal.addAndEdit.time_frame.period.daily");
    }

    // Weekly
    if (item.duration_type === PassportPeriodType.WEEKLY) {
      verdictText = t("modal.addAndEdit.time_frame.period.weekly");
      if (item.repeat_number > 1) {
        verdictText =
          t("modal.addAndEdit.time_frame.verdict.every") +
          ` ${item.repeat_number} ` +
          t(
            `modal.addAndEdit.time_frame.week${item.repeat_number > 1 ? "s" : ""}`
          );
        verdictText = verdictText[0].toUpperCase() + verdictText.slice(1);
      }

      if (item.days.length > 0) {
        tooltipText = `${t("modal.addAndEdit.time_frame.repeat_on")}: ${item.days
          .sort()
          .map(
            (day) => weekDayOptionsFull.find((opt) => opt.value === day)?.label
          )
          .join(", ")}`;
      }
    }

    // Monthly
    if (item.duration_type === PassportPeriodType.MONTHLY) {
      verdictText = t("modal.addAndEdit.time_frame.period.monthly");
      if (item.repeat_number > 1) {
        verdictText =
          t("modal.addAndEdit.time_frame.verdict.every") +
          ` ${item.repeat_number} ` +
          t(
            `modal.addAndEdit.time_frame.month${item.repeat_number > 1 ? "s" : ""}`
          );
        verdictText = verdictText[0].toUpperCase() + verdictText.slice(1);
      }

      if (item.limit_start_time > 0) {
        tooltipText = `${t("modal.addAndEdit.time_frame.repeat_on")}: ${t(
          "modal.addAndEdit.time_frame.date_of_month",
          { date: DateTime.fromSeconds(item.limit_start_time).day }
        )}`;
      }
    }

    return {
      content: (
        <Tooltip
          title={
            !!tooltipText && (
              <p className="text-xs font-semibold text-white text-center">
                {tooltipText}
              </p>
            )
          }
        >
          <div>
            <p className="text-gray-v2-900 font-medium text-sm">
              {item.limit_start_time >= 0
                ? DateTime.fromSeconds(item.limit_start_time).toFormat(
                    "HH:mm:ss"
                  )
                : "N/A"}
              &nbsp;-&nbsp;
              {item.limit_end_time > item.limit_start_time
                ? DateTime.fromSeconds(item.limit_end_time).toFormat("HH:mm:ss")
                : "N/A"}
            </p>
            <p className="text-sm text-gray-v2-600">({verdictText})</p>
          </div>
        </Tooltip>
      ),
    };
  };

  return { renderTimeFrame };
};
